import { useCallback, useState } from 'react';
import type { SortDirection } from '~/components/ui/table';
import type { RouteSort } from '~/hooks/usePaginatedQuery';

/**
 * Turns a `Table` column-header sort into the `RouteSort` a `usePaginatedQuery`
 * consumer forwards to its server query.
 *
 * @param sortFieldByColumnKey UI column key -> backend sort-field whitelist entry. A
 *   column absent from this map resets the sort to the default order, rather than
 *   being rejected — `Table` only calls `onSort` for columns it has marked sortable.
 *
 * @example
 * const { sort, onSort } = useRouteSort({ expectedShipDate: 'expectedShipDate' });
 * usePaginatedQuery({ ..., options: { sort, catchUpOnSortChange: true } });
 * <Table onSort={onSort} ... />
 */
export function useRouteSort(
  sortFieldByColumnKey: Readonly<Partial<Record<string, string>>>
) {
  const [sort, setSort] = useState<RouteSort | undefined>();

  const onSort = useCallback(
    (columnKey: string, direction: SortDirection) => {
      const field = sortFieldByColumnKey[columnKey];
      setSort(
        field && direction
          ? [{ field, direction: direction.toUpperCase() as 'ASC' | 'DESC' }]
          : undefined
      );
    },
    [sortFieldByColumnKey]
  );

  return { sort, onSort };
}
